import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  addDoc,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  doc,
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
// This page used to import ContractorsPage.css, which is a DIFFERENT page's
// global stylesheet. Every rule in LicenseCertificates.css is now scoped under
// .license-certificates-page.
import "./LicenseCertificates.css";
import ScopedPortalPopup from "../components/ScopedPortalPopup";

/* ---------------------------------------------------------------------------
   STATIC LISTS

   The state list was previously 50 hand-written <option> tags repeated three
   times in the JSX. One array, mapped three times, cannot drift out of sync -
   the old copies had already diverged (one was missing DC, one had it in the
   wrong sort position).
--------------------------------------------------------------------------- */
const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DC", "DE", "FL",
  "GA", "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME",
  "MD", "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH",
  "NJ", "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI",
  "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY",
];

/* The starting contents of every editable dropdown. Once someone saves a change
   in Field Setup these are replaced by whatever is stored in Firestore; they
   are only the fallback for a brand new install. */
const DEFAULT_DROPDOWNS = {
  licenseType: [
    "Contractors State License",
    "Home Improvement License",
    "Pest Control License",
    "Secretary of State Business",
    "EIN",
    "Insurance",
    "County/City License",
    "Certificates",
  ],
  licenseCategory: [
    "General Contractor",
    "Pest Control Applicator",
    "Pest Control Business",
    "Electrician",
    "Plumbing",
    "HVAC",
    "Business Filing",
    "Real Estate",
    "PCAM",
    "CAM",
    "Business Insurance",
  ],
  frequency: ["Monthly", "Quarterly", "Yearly", "Every 2 Years"],
};

/* Which dropdowns Field Setup is allowed to edit, and what to call them there.
   Licensee Name is deliberately absent: it is generated from the users
   collection, so editing it by hand here would be overwritten on next load. */
const EDITABLE_DROPDOWNS = [
  { key: "licenseType", label: "License Type" },
  { key: "licenseCategory", label: "License Category" },
  { key: "frequency", label: "Renewal Frequency" },
];

const CUSTOM_FIELD_TYPES = [
  { value: "text", label: "Single line text" },
  { value: "textarea", label: "Paragraph text" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "select", label: "Dropdown" },
];

/* Which dropdowns an extra field can be made conditional on. A field with a
   condition only appears once its parent dropdown is set to one of the values
   listed - e.g. a "County / City name" text box that only shows up when
   License Type is County/City License. */
const CONDITION_PARENTS = [
  { key: "licenseType", label: "License Type" },
  { key: "licenseCategory", label: "License Category" },
  { key: "frequency", label: "Renewal Frequency" },
];

/* Does this extra field apply to this record? A field with no condition always
   applies. */
const fieldApplies = (field, record) => {
  const cond = field.showWhen;
  if (!cond || !cond.field || !Array.isArray(cond.values) || cond.values.length === 0) {
    return true;
  }
  return cond.values.includes(record[cond.field]);
};

const describeCondition = (field) => {
  const cond = field.showWhen;
  if (!cond || !cond.field || !(cond.values || []).length) return null;
  const parent = CONDITION_PARENTS.find((p) => p.key === cond.field);
  return `Shows when ${parent ? parent.label : cond.field} is ${cond.values.join(" or ")}`;
};

/* The columns the register can show, in their default order. Extra fields added
   through Field Setup are appended to this list as "custom:<id>". */
const BASE_COLUMNS = [
  { key: "licensee", label: "Licensee" },
  { key: "licenseType", label: "Type" },
  { key: "licenseCategory", label: "Category" },
  { key: "licenseNumber", label: "Number" },
  { key: "expirationDate", label: "Expires" },
  { key: "stateOfLicense", label: "State" },
  { key: "status", label: "Status" },
  { key: "classification", label: "Classification" },
  { key: "frequency", label: "Frequency" },
  { key: "cost", label: "Cost" },
  { key: "licensingAgencyName", label: "Agency" },
  { key: "licensingAgencyState", label: "Agency State" },
];

/* Columns shown on a fresh install, before anyone has touched Field Setup. */
const DEFAULT_VISIBLE_COLUMNS = [
  "licensee",
  "licenseType",
  "licenseCategory",
  "licenseNumber",
  "expirationDate",
  "stateOfLicense",
  "status",
];

const customColumnKey = (fieldId) => `custom:${fieldId}`;

/* Every column that COULD be shown, given the extra fields currently defined. */
const availableColumns = (customFields) => [
  ...BASE_COLUMNS,
  ...customFields.map((f) => ({ key: customColumnKey(f.id), label: f.label })),
];

/* Saved column preferences are reconciled against what actually exists: columns
   for deleted extra fields are dropped, and newly added ones are appended so
   they can be switched on. Without this a saved list would silently freeze the
   register at whatever columns existed the day it was saved. */
const resolveColumns = (saved, customFields) => {
  const available = availableColumns(customFields);
  const byKey = new Map(available.map((c) => [c.key, c]));
  const seen = new Set();
  const resolved = [];

  (saved || []).forEach((entry) => {
    const col = byKey.get(entry.key);
    if (!col || seen.has(entry.key)) return;
    seen.add(entry.key);
    resolved.push({ key: col.key, label: col.label, visible: entry.visible !== false });
  });

  available.forEach((col) => {
    if (seen.has(col.key)) return;
    resolved.push({
      key: col.key,
      label: col.label,
      // A brand new extra field is shown by default; an untouched base column
      // follows the starting set.
      visible: col.key.startsWith("custom:")
        ? true
        : DEFAULT_VISIBLE_COLUMNS.includes(col.key),
    });
  });

  return resolved;
};

const CONFIG_COLLECTION = "licenseConfig";
const CONFIG_DOC = "fieldSettings";

const EMPTY_LICENSE = {
  licenseType: "",
  licenseTypeOther: "",
  licenseCategory: "",
  licenseCategoryOther: "",
  licenseeName: "",
  licenseeNameOther: "",
  companyName: "",
  classification: "",
  licenseNumber: "",
  expirationDate: "",
  frequency: "",
  otherFrequency: "",
  cost: "",
  stateOfLicense: "",
  licensingAgencyName: "",
  licensingAgencyAddress: "",
  licensingAgencyCity: "",
  licensingAgencyState: "",
  licensingAgencyZipCode: "",
  licensingAgencyPhone: "",
  licensingAgencyEmail: "",
  licensingAgencyWebsite: "",
  companyAddress: "",
  notes: "",
  status: "Active",
  canceled: false,
  fileUpload: [],
  // Values for any field added through Field Setup, keyed by that field's id.
  custom: {},
};

const MANDATORY_FIELDS = [
  { key: "licenseType", label: "License Type" },
  { key: "licenseCategory", label: "License Category" },
  { key: "licenseeName", label: "Licensee Name" },
  { key: "companyName", label: "Company Name on License" },
  { key: "licenseNumber", label: "License Number" },
  { key: "stateOfLicense", label: "State of License" },
  { key: "licensingAgencyName", label: "Licensing Agency Name" },
];

/* ---------------------------------------------------------------------------
   DATE HELPERS

   Expiration dates are stored as "YYYY-MM-DD" strings. new Date("2025-03-10")
   parses as UTC midnight, which renders as the PREVIOUS day for anyone west of
   Greenwich, so the date is built from its parts as a local date instead.
--------------------------------------------------------------------------- */
const parseYmd = (ymd) => {
  if (!ymd || typeof ymd !== "string") return null;
  const [y, m, d] = ymd.slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const formatYmd = (ymd) => {
  const date = parseYmd(ymd);
  if (!date) return "";
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

// Returns { state, days, label } where state is expired | soon | ok | none.
const expiryInfo = (expirationDate) => {
  const exp = parseYmd(expirationDate);
  if (!exp) return { state: "none", days: null, label: "No date" };

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days = Math.round((exp - today) / 86400000);

  if (days < 0) {
    const ago = Math.abs(days);
    return { state: "expired", days, label: `Expired ${ago}d ago` };
  }
  if (days <= 60) {
    return { state: "soon", days, label: days === 0 ? "Expires today" : `${days}d left` };
  }
  return { state: "ok", days, label: "Current" };
};

/* Attachments used to be stored as bare URL strings and are now stored as
   { name, url } so the original filename can be shown. Records written before
   the change still hold strings, so both shapes are read here. */
const attachmentInfo = (entry, index) => {
  if (!entry) return null;
  if (typeof entry === "string") return { name: `File ${index + 1}`, url: entry };
  return { name: entry.name || `File ${index + 1}`, url: entry.url };
};

const makeId = (prefix) =>
  `${prefix}_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;

const LicenseCertificate = () => {
  const [licenseCertificates, setLicenseCertificates] = useState([]);
  const [newLicenseCertificate, setNewLicenseCertificate] = useState(EMPTY_LICENSE);
  const [selectedLicenseCertificate, setSelectedLicenseCertificate] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedLicenseCertificate, setEditedLicenseCertificate] = useState({});
  const [showAddPopup, setShowAddPopup] = useState(false);
  const [showDetailsPopup, setShowDetailsPopup] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  /* Toolbar */
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [sortKey, setSortKey] = useState("expirationDate");
  const [hideCanceled, setHideCanceled] = useState(true);

  /* Field Setup panel. `fieldConfig` is what the page is currently rendering
     with; `draftConfig` is the working copy inside the panel, so closing the
     panel without saving changes nothing. */
  const [fieldConfig, setFieldConfig] = useState({
    dropdowns: DEFAULT_DROPDOWNS,
    customFields: [],
    columns: resolveColumns([], []),
  });
  const [showFieldPanel, setShowFieldPanel] = useState(false);
  const [draftConfig, setDraftConfig] = useState(null);
  const [newOptionText, setNewOptionText] = useState({});
  const [newCustomField, setNewCustomField] = useState({
    label: "",
    type: "text",
    options: "",
    conditionField: "",
    conditionValues: [],
    conditionDraftValue: "",
  });

  /* Upload failures used to be swallowed by a bare console.error, so a file
     that Storage rejected looked exactly like one that saved. */
  const [uploadError, setUploadError] = useState("");

  /* One confirmation sheet serves every destructive or saving action:
     { title, message, confirmLabel, tone, onConfirm } */
  const [confirmState, setConfirmState] = useState(null);

  const storage = getStorage();

  const askConfirm = (config) => setConfirmState(config);
  const closeConfirm = () => setConfirmState(null);

  /* ---------------------------------------------------------------- load -- */

  useEffect(() => {
    const load = async () => {
      try {
        const [licenseSnap, userSnap] = await Promise.all([
          getDocs(collection(db, "licenseCertificates")),
          getDocs(collection(db, "users")),
        ]);

        setLicenseCertificates(
          licenseSnap.docs.map((d) => ({ id: d.id, ...d.data() }))
        );
        setUsers(userSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (error) {
        console.error("Error loading license certificates:", error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Field config lives in its own document so it can be read and written
  // independently of the licenses themselves.
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const snap = await getDoc(doc(db, CONFIG_COLLECTION, CONFIG_DOC));
        if (snap.exists()) {
          const data = snap.data();
          const customFields = Array.isArray(data.customFields) ? data.customFields : [];
          setFieldConfig({
            dropdowns: { ...DEFAULT_DROPDOWNS, ...(data.dropdowns || {}) },
            customFields,
            columns: resolveColumns(data.columns, customFields),
          });
        }
      } catch (error) {
        console.error("Error loading field configuration:", error);
      }
    };
    loadConfig();
  }, []);

  /* ------------------------------------------------------------- inputs -- */

  /* Shared by the Add and Edit change handlers.

     A conditional extra field that no longer applies has its value dropped, for
     the same reason the "Other" boxes are cleared: a hidden field must never
     quietly save a value the form is no longer showing. Someone who types a
     county name, switches License Type away and switches back will have to type
     it again - the alternative is a record carrying a county it does not have. */
  const applyDependentClears = (prev, name, value) => {
    const next = { ...prev, [name]: value };

    if (name === "licenseType" && value !== "Other") next.licenseTypeOther = "";
    if (name === "licenseCategory" && value !== "Other") next.licenseCategoryOther = "";
    if (name === "licenseeName" && value !== "Other") next.licenseeNameOther = "";
    if (name === "frequency" && value !== "Other") next.otherFrequency = "";

    const isParent = CONDITION_PARENTS.some((p) => p.key === name);
    if (isParent && next.custom) {
      const custom = { ...next.custom };
      fieldConfig.customFields.forEach((field) => {
        if (!fieldApplies(field, next) && custom[field.id] !== undefined) {
          delete custom[field.id];
        }
      });
      next.custom = custom;
    }

    return next;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewLicenseCertificate((prev) => applyDependentClears(prev, name, value));
  };

  const handleInputChangeForEdit = (e) => {
    const { name, value } = e.target;
    setEditedLicenseCertificate((prev) => applyDependentClears(prev, name, value));
  };

  const handleCustomChange = (fieldId, value, forEdit) => {
    const setter = forEdit ? setEditedLicenseCertificate : setNewLicenseCertificate;
    setter((prev) => ({
      ...prev,
      custom: { ...(prev.custom || {}), [fieldId]: value },
    }));
  };

  /* Attachments are uploaded to Storage immediately and the resulting download
     URLs are held on the record, which is what gets written to Firestore on
     save. Edit can attach too, so a record whose upload failed can be repaired
     without being deleted and re-entered.

     Note the file name is made unique. The old path was
     licenseCertificates/<company>/<filename>, so a second "renewal.pdf" for the
     same company overwrote the first one and both records ended up pointing at
     the same file. */
  const handleFileUpload = async (e, forEdit) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const setter = forEdit ? setEditedLicenseCertificate : setNewLicenseCertificate;
    const source = forEdit ? editedLicenseCertificate : newLicenseCertificate;
    const folder = (source.companyName || "unfiled").replace(/[/#?[\]]/g, "-");

    setUploading(true);
    setUploadError("");
    try {
      const uploaded = [];
      for (const file of files) {
        const unique = `${Date.now()}-${file.name}`;
        const storageRef = ref(storage, `licenseCertificates/${folder}/${unique}`);
        await uploadBytes(storageRef, file);
        uploaded.push({ name: file.name, url: await getDownloadURL(storageRef) });
      }
      setter((prev) => ({
        ...prev,
        fileUpload: [...(prev.fileUpload || []), ...uploaded],
      }));
    } catch (error) {
      console.error("Error uploading files:", error);
      setUploadError(
        error && error.code === "storage/unauthorized"
          ? "Storage rejected the upload. Your Firebase Storage rules are blocking writes to licenseCertificates/."
          : `Upload failed: ${error && error.message ? error.message : "unknown error"}`
      );
    } finally {
      setUploading(false);
      // Clearing the input lets the same file be picked again after a failure.
      e.target.value = "";
    }
  };

  const removeAttachment = (index, forEdit) => {
    const setter = forEdit ? setEditedLicenseCertificate : setNewLicenseCertificate;
    setter((prev) => ({
      ...prev,
      fileUpload: (prev.fileUpload || []).filter((_, i) => i !== index),
    }));
  };

  /* -------------------------------------------------------------- save --- */

  const missingMandatory = (record) =>
    MANDATORY_FIELDS.filter((f) => !String(record[f.key] || "").trim());

  // Every save goes through the confirmation sheet first. requestAdd only
  // validates and opens the sheet; commitAdd is what actually writes.
  const requestAdd = () => {
    const missing = missingMandatory(newLicenseCertificate);
    if (missing.length) {
      askConfirm({
        title: "Some required fields are empty",
        message: `Fill these in before saving: ${missing.map((f) => f.label).join(", ")}.`,
        confirmLabel: null,
        cancelLabel: "Back to form",
      });
      return;
    }

    askConfirm({
      title: "Save this license?",
      message: `${newLicenseCertificate.licenseType} for ${newLicenseCertificate.companyName} will be added to the register.`,
      confirmLabel: "Yes, save it",
      tone: "green",
      onConfirm: commitAdd,
    });
  };

  const commitAdd = async () => {
    setSaving(true);
    try {
      const payload = { ...newLicenseCertificate };
      const docRef = await addDoc(collection(db, "licenseCertificates"), payload);
      setLicenseCertificates((prev) => [...prev, { id: docRef.id, ...payload }]);
      setNewLicenseCertificate(EMPTY_LICENSE);
      setShowAddPopup(false);
      closeConfirm();
    } catch (error) {
      console.error("Error adding license certificate:", error);
      askConfirm({
        title: "That didn't save",
        message: "The record could not be written. Check your connection and try again.",
        confirmLabel: null,
        cancelLabel: "Close",
      });
    } finally {
      setSaving(false);
    }
  };

  const requestSaveChanges = () => {
    const missing = missingMandatory(editedLicenseCertificate);
    if (missing.length) {
      askConfirm({
        title: "Some required fields are empty",
        message: `Fill these in before saving: ${missing.map((f) => f.label).join(", ")}.`,
        confirmLabel: null,
        cancelLabel: "Back to form",
      });
      return;
    }

    askConfirm({
      title: "Save these changes?",
      message: "The existing record will be updated with what you have entered.",
      confirmLabel: "Yes, save changes",
      tone: "green",
      onConfirm: commitSaveChanges,
    });
  };

  const commitSaveChanges = async () => {
    setSaving(true);
    try {
      const { id, ...rest } = editedLicenseCertificate;
      await updateDoc(doc(db, "licenseCertificates", id), rest);
      setLicenseCertificates((prev) =>
        prev.map((item) => (item.id === id ? { ...editedLicenseCertificate } : item))
      );
      setIsEditing(false);
      setShowDetailsPopup(false);
      setSelectedLicenseCertificate(null);
      closeConfirm();
    } catch (error) {
      console.error("Error saving license certificate changes:", error);
    } finally {
      setSaving(false);
    }
  };

  const requestDelete = () => {
    askConfirm({
      title: "Delete this license?",
      message:
        "This removes the record permanently. If the license simply lapsed, use Cancel on the list instead so the history is kept.",
      confirmLabel: "Yes, delete",
      tone: "red",
      onConfirm: commitDelete,
    });
  };

  const commitDelete = async () => {
    setSaving(true);
    try {
      await deleteDoc(doc(db, "licenseCertificates", selectedLicenseCertificate.id));
      setLicenseCertificates((prev) =>
        prev.filter((item) => item.id !== selectedLicenseCertificate.id)
      );
      setShowDetailsPopup(false);
      setSelectedLicenseCertificate(null);
      closeConfirm();
    } catch (error) {
      console.error("Error deleting license certificate:", error);
    } finally {
      setSaving(false);
    }
  };

  const requestToggleCanceled = (record) => {
    const turningOff = !record.canceled;
    askConfirm({
      title: turningOff ? "Mark this license canceled?" : "Reactivate this license?",
      message: turningOff
        ? "It stays in the register but is hidden while \u201cHide canceled\u201d is on."
        : "It will show as active again in the register.",
      confirmLabel: turningOff ? "Yes, cancel it" : "Yes, reactivate",
      tone: turningOff ? "red" : "green",
      onConfirm: () => commitToggleCanceled(record),
    });
  };

  const commitToggleCanceled = async (record) => {
    const nextCanceled = !record.canceled;
    setSaving(true);
    try {
      await updateDoc(doc(db, "licenseCertificates", record.id), {
        canceled: nextCanceled,
        status: nextCanceled ? "Canceled" : "Active",
      });
      setLicenseCertificates((prev) =>
        prev.map((item) =>
          item.id === record.id
            ? {
                ...item,
                canceled: nextCanceled,
                status: nextCanceled ? "Canceled" : "Active",
              }
            : item
        )
      );
      closeConfirm();
    } catch (error) {
      console.error("Error updating license status:", error);
    } finally {
      setSaving(false);
    }
  };

  /* --------------------------------------------------------- field setup -- */

  const openFieldPanel = () => {
    // Deep-ish copy so edits in the panel do not mutate live config.
    setDraftConfig({
      dropdowns: Object.fromEntries(
        Object.entries(fieldConfig.dropdowns).map(([k, v]) => [k, [...v]])
      ),
      customFields: fieldConfig.customFields.map((f) => ({
        ...f,
        showWhen: f.showWhen ? { ...f.showWhen, values: [...f.showWhen.values] } : null,
      })),
      columns: fieldConfig.columns.map((c) => ({ ...c })),
    });
    setNewOptionText({});
    setNewCustomField({
      label: "",
      type: "text",
      options: "",
      conditionField: "",
      conditionValues: [],
      conditionDraftValue: "",
    });
    setShowFieldPanel(true);
  };

  /* ------------------------------------------------------ column manager -- */

  const toggleColumn = (key) => {
    setDraftConfig((prev) => ({
      ...prev,
      columns: prev.columns.map((c) =>
        c.key === key ? { ...c, visible: !c.visible } : c
      ),
    }));
  };

  const moveColumn = (key, delta) => {
    setDraftConfig((prev) => {
      const columns = [...prev.columns];
      const index = columns.findIndex((c) => c.key === key);
      const target = index + delta;
      if (index < 0 || target < 0 || target >= columns.length) return prev;
      [columns[index], columns[target]] = [columns[target], columns[index]];
      return { ...prev, columns };
    });
  };

  const setAllColumns = (visible) => {
    setDraftConfig((prev) => ({
      ...prev,
      columns: prev.columns.map((c) => ({ ...c, visible })),
    }));
  };

  const addDropdownOption = (key) => {
    const value = String(newOptionText[key] || "").trim();
    if (!value) return;
    const existing = draftConfig.dropdowns[key] || [];
    // Case-insensitive duplicate check, otherwise "HVAC" and "hvac" both land
    // in the list and the dropdown looks broken.
    if (existing.some((o) => o.toLowerCase() === value.toLowerCase())) {
      setNewOptionText((prev) => ({ ...prev, [key]: "" }));
      return;
    }
    setDraftConfig((prev) => ({
      ...prev,
      dropdowns: { ...prev.dropdowns, [key]: [...existing, value] },
    }));
    setNewOptionText((prev) => ({ ...prev, [key]: "" }));
  };

  const removeDropdownOption = (key, option) => {
    setDraftConfig((prev) => ({
      ...prev,
      dropdowns: {
        ...prev.dropdowns,
        [key]: prev.dropdowns[key].filter((o) => o !== option),
      },
    }));
  };

  const addCustomField = () => {
    const label = newCustomField.label.trim();
    if (!label) return;
    const field = {
      id: makeId("cf"),
      label,
      type: newCustomField.type,
      options:
        newCustomField.type === "select"
          ? newCustomField.options
              .split(",")
              .map((o) => o.trim())
              .filter(Boolean)
          : [],
      showWhen:
        newCustomField.conditionField && newCustomField.conditionValues.length
          ? {
              field: newCustomField.conditionField,
              values: [...newCustomField.conditionValues],
            }
          : null,
    };
    setDraftConfig((prev) => {
      const customFields = [...prev.customFields, field];
      return {
        ...prev,
        customFields,
        // resolveColumns appends the new field's column so it can be switched
        // on straight away, without disturbing the existing order.
        columns: resolveColumns(prev.columns, customFields),
      };
    });
    setNewCustomField({
      label: "",
      type: "text",
      options: "",
      conditionField: "",
      conditionValues: [],
      conditionDraftValue: "",
    });
  };

  const removeCustomField = (fieldId) => {
    setDraftConfig((prev) => {
      const customFields = prev.customFields.filter((f) => f.id !== fieldId);
      return {
        ...prev,
        customFields,
        columns: resolveColumns(prev.columns, customFields),
      };
    });
  };

  const addConditionValue = () => {
    const value = newCustomField.conditionDraftValue;
    if (!value || newCustomField.conditionValues.includes(value)) return;
    setNewCustomField((prev) => ({
      ...prev,
      conditionValues: [...prev.conditionValues, value],
      conditionDraftValue: "",
    }));
  };

  const removeConditionValue = (value) => {
    setNewCustomField((prev) => ({
      ...prev,
      conditionValues: prev.conditionValues.filter((v) => v !== value),
    }));
  };

  const requestSaveFieldConfig = () => {
    const removedFields = fieldConfig.customFields.filter(
      (f) => !draftConfig.customFields.some((d) => d.id === f.id)
    );

    askConfirm({
      title: "Apply these field changes?",
      message: removedFields.length
        ? `This updates the Add and Edit forms for everyone. Removing ${removedFields
            .map((f) => f.label)
            .join(", ")} hides the field from the forms; values already saved on existing licenses are kept.`
        : "This updates the Add and Edit forms for everyone.",
      confirmLabel: "Yes, apply",
      tone: "green",
      onConfirm: commitSaveFieldConfig,
    });
  };

  const commitSaveFieldConfig = async () => {
    setSaving(true);
    try {
      // Column labels are derived from BASE_COLUMNS and the extra field names,
      // so only the key and the on/off state are stored. Persisting the label
      // would leave a renamed field showing its old heading.
      const payload = {
        dropdowns: draftConfig.dropdowns,
        customFields: draftConfig.customFields,
        columns: draftConfig.columns.map((c) => ({ key: c.key, visible: c.visible })),
      };
      await setDoc(doc(db, CONFIG_COLLECTION, CONFIG_DOC), payload, { merge: false });
      setFieldConfig({
        dropdowns: draftConfig.dropdowns,
        customFields: draftConfig.customFields,
        columns: resolveColumns(payload.columns, draftConfig.customFields),
      });
      setShowFieldPanel(false);
      closeConfirm();
    } catch (error) {
      console.error("Error saving field configuration:", error);
      askConfirm({
        title: "Field setup didn't save",
        message: "The configuration could not be written. Your changes are still on screen.",
        confirmLabel: null,
        cancelLabel: "Close",
      });
    } finally {
      setSaving(false);
    }
  };

  /* -------------------------------------------------------------- popups -- */

  const handleDetailsClick = (record) => {
    setSelectedLicenseCertificate(record);
    setEditedLicenseCertificate({ custom: {}, ...record });
    setShowDetailsPopup(true);
    setIsEditing(false);
  };

  const closeAddPopup = () => {
    setShowAddPopup(false);
    setNewLicenseCertificate(EMPTY_LICENSE);
  };

  const closeDetailsPopup = () => {
    setShowDetailsPopup(false);
    setSelectedLicenseCertificate(null);
    setIsEditing(false);
  };

  /* --------------------------------------------------- derived list data -- */

  const licenseeOptions = useMemo(
    () =>
      users
        .map((u) => `${u.firstName || ""} ${u.lastName || ""}`.trim())
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b)),
    [users]
  );

  const visibleLicenses = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();

    let rows = licenseCertificates.filter((item) => {
      if (hideCanceled && item.canceled) return false;
      if (typeFilter && item.licenseType !== typeFilter) return false;
      if (!q) return true;

      return [
        item.licenseType,
        item.licenseTypeOther,
        item.licenseCategory,
        item.licenseeName,
        item.licenseeNameOther,
        item.companyName,
        item.licenseNumber,
        item.licensingAgencyName,
        item.stateOfLicense,
        item.licensingAgencyState,
      ]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });

    rows = [...rows].sort((a, b) => {
      if (sortKey === "expirationDate") {
        // Records with no expiry sort last rather than jumping to the top,
        // which is what an empty string does under a plain string compare.
        const av = a.expirationDate || "9999-12-31";
        const bv = b.expirationDate || "9999-12-31";
        return av.localeCompare(bv);
      }
      return String(a[sortKey] || "").localeCompare(String(b[sortKey] || ""));
    });

    return rows;
  }, [licenseCertificates, searchQuery, typeFilter, sortKey, hideCanceled]);

  const stats = useMemo(() => {
    const live = licenseCertificates.filter((l) => !l.canceled);
    let expired = 0;
    let soon = 0;
    live.forEach((l) => {
      const info = expiryInfo(l.expirationDate);
      if (info.state === "expired") expired += 1;
      else if (info.state === "soon") soon += 1;
    });
    return { total: live.length, expired, soon, canceled: licenseCertificates.length - live.length };
  }, [licenseCertificates]);

  const visibleColumns = useMemo(
    () => fieldConfig.columns.filter((c) => c.visible),
    [fieldConfig.columns]
  );

  /* ------------------------------------------------------ shared renders -- */

  const dash = <span className="lc-muted">—</span>;

  /* One cell of the register. `info` is the already-computed expiry state for
     this row, passed in so it is not recalculated per column. */
  const renderCell = (item, col, info) => {
    if (col.key.startsWith("custom:")) {
      const fieldId = col.key.slice("custom:".length);
      const field = fieldConfig.customFields.find((f) => f.id === fieldId);
      // A conditional field shows a dash on rows it does not apply to, rather
      // than an empty cell that looks like missing data.
      if (field && !fieldApplies(field, item)) return dash;
      return (item.custom || {})[fieldId] || dash;
    }

    switch (col.key) {
      case "licensee":
        return (
          <>
            <span className="lc-strong">
              {item.licenseeName === "Other"
                ? item.licenseeNameOther || "Other"
                : item.licenseeName || "—"}
            </span>
            <span className="lc-sub">{item.companyName}</span>
          </>
        );

      case "licenseType":
        return item.licenseType === "Other"
          ? item.licenseTypeOther || "Other"
          : item.licenseType || dash;

      case "licenseCategory":
        return item.licenseCategory === "Other"
          ? item.licenseCategoryOther || "Other"
          : item.licenseCategory || dash;

      case "frequency":
        return item.frequency === "Other"
          ? item.otherFrequency || "Other"
          : item.frequency || dash;

      case "expirationDate":
        if (!item.expirationDate) return dash;
        return (
          <>
            <span className="lc-strong">{formatYmd(item.expirationDate)}</span>
            <span
              className={`lc-chip lc-chip-${info.state === "none" ? "off" : info.state}`}
            >
              {info.label}
            </span>
          </>
        );

      case "cost":
        return item.cost ? `$${item.cost}` : dash;

      case "status":
        return (
          <span className={`lc-chip ${item.canceled ? "lc-chip-off" : "lc-chip-ok"}`}>
            {item.canceled ? "Canceled" : item.status || "Active"}
          </span>
        );

      default:
        return item[col.key] || dash;
    }
  };

  // One function renders the form for both Add and Edit so the two can never
  // drift apart the way the original file's two hand-maintained copies had.
  const renderForm = (record, onChange, forEdit) => {
    const dd = fieldConfig.dropdowns;

    return (
      <div className="lc-form-grid">
        <div className="lc-section-head">License</div>

        <div>
          <label htmlFor="lc-licenseType">
            License Type <span className="lc-required">*</span>
          </label>
          <select
            id="lc-licenseType"
            name="licenseType"
            value={record.licenseType || ""}
            onChange={onChange}
          >
            <option value="">Select license type</option>
            {dd.licenseType.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
            <option value="Other">Other</option>
          </select>
          {record.licenseType === "Other" && (
            <input
              className="lc-other-input"
              name="licenseTypeOther"
              value={record.licenseTypeOther || ""}
              onChange={onChange}
              placeholder="Describe the license type"
            />
          )}
        </div>

        <div>
          <label htmlFor="lc-licenseCategory">
            License Category <span className="lc-required">*</span>
          </label>
          <select
            id="lc-licenseCategory"
            name="licenseCategory"
            value={record.licenseCategory || ""}
            onChange={onChange}
          >
            <option value="">Select category</option>
            {dd.licenseCategory.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
            <option value="Other">Other</option>
          </select>
          {record.licenseCategory === "Other" && (
            <input
              className="lc-other-input"
              name="licenseCategoryOther"
              value={record.licenseCategoryOther || ""}
              onChange={onChange}
              placeholder="Describe the category"
            />
          )}
        </div>

        <div>
          <label htmlFor="lc-licenseeName">
            Licensee Name <span className="lc-required">*</span>
          </label>
          <select
            id="lc-licenseeName"
            name="licenseeName"
            value={record.licenseeName || ""}
            onChange={onChange}
          >
            <option value="">Select licensee</option>
            {licenseeOptions.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
            <option value="Other">Other</option>
          </select>
          {record.licenseeName === "Other" && (
            <input
              className="lc-other-input"
              name="licenseeNameOther"
              value={record.licenseeNameOther || ""}
              onChange={onChange}
              placeholder="Name of the licensee"
            />
          )}
        </div>

        <div>
          <label htmlFor="lc-companyName">
            Company Name on License <span className="lc-required">*</span>
          </label>
          <input
            id="lc-companyName"
            name="companyName"
            value={record.companyName || ""}
            onChange={onChange}
          />
        </div>

        <div>
          <label htmlFor="lc-classification">Classification</label>
          <input
            id="lc-classification"
            name="classification"
            value={record.classification || ""}
            onChange={onChange}
            placeholder="e.g. B - General Building"
          />
        </div>

        <div>
          <label htmlFor="lc-licenseNumber">
            License Number <span className="lc-required">*</span>
          </label>
          <input
            id="lc-licenseNumber"
            name="licenseNumber"
            value={record.licenseNumber || ""}
            onChange={onChange}
          />
        </div>

        <div>
          <label htmlFor="lc-stateOfLicense">
            State of License <span className="lc-required">*</span>
          </label>
          <select
            id="lc-stateOfLicense"
            name="stateOfLicense"
            value={record.stateOfLicense || ""}
            onChange={onChange}
          >
            <option value="">Select state</option>
            {US_STATES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="lc-status">Status</label>
          <select
            id="lc-status"
            name="status"
            value={record.status || "Active"}
            onChange={onChange}
          >
            <option value="Active">Active</option>
            <option value="Terminated">Terminated</option>
            <option value="Canceled">Canceled</option>
          </select>
        </div>

        <div className="lc-section-head">Renewal</div>

        <div>
          <label htmlFor="lc-expirationDate">Expiration Date</label>
          <input
            id="lc-expirationDate"
            name="expirationDate"
            type="date"
            value={record.expirationDate || ""}
            onChange={onChange}
          />
          <p className="lc-hint">Rows turn amber 60 days out and red once past.</p>
        </div>

        <div>
          <label htmlFor="lc-frequency">Renewal Frequency</label>
          <select
            id="lc-frequency"
            name="frequency"
            value={record.frequency || ""}
            onChange={onChange}
          >
            <option value="">Select frequency</option>
            {dd.frequency.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
            <option value="Other">Other</option>
          </select>
          {record.frequency === "Other" && (
            <input
              className="lc-other-input"
              name="otherFrequency"
              value={record.otherFrequency || ""}
              onChange={onChange}
              placeholder="Describe the frequency"
            />
          )}
        </div>

        <div>
          <label htmlFor="lc-cost">Renewal Cost</label>
          <input
            id="lc-cost"
            name="cost"
            type="number"
            value={record.cost || ""}
            onChange={onChange}
            placeholder="0.00"
          />
        </div>

        <div className="lc-section-head">Licensing Agency</div>

        <div>
          <label htmlFor="lc-licensingAgencyName">
            Agency Name <span className="lc-required">*</span>
          </label>
          <input
            id="lc-licensingAgencyName"
            name="licensingAgencyName"
            value={record.licensingAgencyName || ""}
            onChange={onChange}
          />
        </div>

        <div>
          <label htmlFor="lc-licensingAgencyWebsite">Agency Website</label>
          <input
            id="lc-licensingAgencyWebsite"
            name="licensingAgencyWebsite"
            value={record.licensingAgencyWebsite || ""}
            onChange={onChange}
            placeholder="https://"
          />
        </div>

        <div>
          <label htmlFor="lc-licensingAgencyAddress">Agency Address</label>
          <input
            id="lc-licensingAgencyAddress"
            name="licensingAgencyAddress"
            value={record.licensingAgencyAddress || ""}
            onChange={onChange}
          />
        </div>

        <div>
          <label htmlFor="lc-licensingAgencyCity">Agency City</label>
          <input
            id="lc-licensingAgencyCity"
            name="licensingAgencyCity"
            value={record.licensingAgencyCity || ""}
            onChange={onChange}
          />
        </div>

        <div>
          <label htmlFor="lc-licensingAgencyState">Agency State</label>
          <select
            id="lc-licensingAgencyState"
            name="licensingAgencyState"
            value={record.licensingAgencyState || ""}
            onChange={onChange}
          >
            <option value="">Select state</option>
            {US_STATES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="lc-licensingAgencyZipCode">Agency Zip Code</label>
          <input
            id="lc-licensingAgencyZipCode"
            name="licensingAgencyZipCode"
            value={record.licensingAgencyZipCode || ""}
            onChange={onChange}
          />
        </div>

        <div>
          <label htmlFor="lc-licensingAgencyPhone">Agency Phone</label>
          <input
            id="lc-licensingAgencyPhone"
            name="licensingAgencyPhone"
            value={record.licensingAgencyPhone || ""}
            onChange={onChange}
          />
        </div>

        <div>
          <label htmlFor="lc-licensingAgencyEmail">Agency Email</label>
          <input
            id="lc-licensingAgencyEmail"
            name="licensingAgencyEmail"
            type="email"
            value={record.licensingAgencyEmail || ""}
            onChange={onChange}
          />
        </div>

        <div className="lc-section-head">Company &amp; Notes</div>

        <div className="lc-span-2">
          <label htmlFor="lc-companyAddress">Company Address</label>
          <textarea
            id="lc-companyAddress"
            name="companyAddress"
            value={record.companyAddress || ""}
            onChange={onChange}
          />
        </div>

        <div className="lc-span-2">
          <label htmlFor="lc-notes">Notes</label>
          <textarea
            id="lc-notes"
            name="notes"
            value={record.notes || ""}
            onChange={onChange}
          />
        </div>

        {/* Anything added through Field Setup renders here. Fields carrying a
            condition only appear once their parent dropdown matches. */}
        {fieldConfig.customFields.some((f) => fieldApplies(f, record)) && (
          <div className="lc-section-head">Additional Information</div>
        )}
        {fieldConfig.customFields.filter((f) => fieldApplies(f, record)).map((field) => {
          const value = (record.custom || {})[field.id] || "";
          const inputId = `lc-custom-${field.id}`;
          return (
            <div
              key={field.id}
              className={field.type === "textarea" ? "lc-span-2" : undefined}
            >
              <label htmlFor={inputId}>{field.label}</label>
              {field.type === "textarea" ? (
                <textarea
                  id={inputId}
                  value={value}
                  onChange={(e) => handleCustomChange(field.id, e.target.value, forEdit)}
                />
              ) : field.type === "select" ? (
                <select
                  id={inputId}
                  value={value}
                  onChange={(e) => handleCustomChange(field.id, e.target.value, forEdit)}
                >
                  <option value="">Select an option</option>
                  {(field.options || []).map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  id={inputId}
                  type={field.type === "number" ? "number" : field.type === "date" ? "date" : "text"}
                  value={value}
                  onChange={(e) => handleCustomChange(field.id, e.target.value, forEdit)}
                />
              )}
            </div>
          );
        })}

        <div className="lc-section-head">Attachments</div>

        <div className="lc-span-2">
          <label htmlFor={`lc-fileUpload-${forEdit ? "edit" : "add"}`}>Add files</label>
          <input
            id={`lc-fileUpload-${forEdit ? "edit" : "add"}`}
            type="file"
            multiple
            accept=".pdf,.jpeg,.jpg,.heic,.png,.xlsx"
            onChange={(e) => handleFileUpload(e, forEdit)}
            disabled={uploading}
          />
          <p className="lc-hint">
            {uploading ? "Uploading..." : "PDF, image or spreadsheet."}
          </p>
          {uploadError && <p className="lc-error">{uploadError}</p>}

          {(record.fileUpload || []).length > 0 ? (
            <ul className="lc-attach-list">
              {(record.fileUpload || []).map((entry, i) => {
                const file = attachmentInfo(entry, i);
                if (!file) return null;
                return (
                  <li className="lc-attach-item" key={file.url || i}>
                    <a href={file.url} target="_blank" rel="noopener noreferrer">
                      {file.name}
                    </a>
                    <button
                      type="button"
                      className="lc-pill-remove"
                      aria-label={`Remove ${file.name}`}
                      onClick={() => removeAttachment(i, forEdit)}
                    >
                      ×
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="lc-hint">Nothing attached yet.</p>
          )}
        </div>
      </div>
    );
  };

  /* -------------------------------------------------------------- render -- */

  return (
    <div className="license-certificates-page">
      <header className="lc-header">
        <div>
          <h1 className="lc-title">Licenses &amp; Certificates</h1>
          <p className="lc-subtitle">
            Every license and certificate the company holds, with renewal dates
            tracked. Rows flag amber 60 days before expiry and red once the date
            has passed.
          </p>
        </div>
        <div className="lc-header-actions">
          <button className="blue-button" onClick={() => setShowAddPopup(true)}>
            Add License
          </button>
          <button className="grey-button" onClick={openFieldPanel}>
            Field Setup
          </button>
        </div>
      </header>

      <div className="lc-stats">
        <div className="lc-stat">
          <span className="lc-stat-value">{stats.total}</span>
          <span className="lc-stat-label">Active records</span>
        </div>
        <div className="lc-stat lc-stat-expired">
          <span className="lc-stat-value">{stats.expired}</span>
          <span className="lc-stat-label">Expired</span>
        </div>
        <div className="lc-stat lc-stat-soon">
          <span className="lc-stat-value">{stats.soon}</span>
          <span className="lc-stat-label">Due within 60 days</span>
        </div>
        <div className="lc-stat lc-stat-ok">
          <span className="lc-stat-value">{stats.canceled}</span>
          <span className="lc-stat-label">Canceled</span>
        </div>
      </div>

      <div className="search-sort-container">
        <div className="lc-field lc-field-grow">
          <span className="lc-field-label" id="lc-search-label">
            Search
          </span>
          <input
            aria-labelledby="lc-search-label"
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Licensee, company, number, agency or state"
          />
        </div>

        <div className="lc-field">
          <span className="lc-field-label" id="lc-type-label">
            License type
          </span>
          <select
            aria-labelledby="lc-type-label"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="">All types</option>
            {fieldConfig.dropdowns.licenseType.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
            <option value="Other">Other</option>
          </select>
        </div>

        <div className="lc-field">
          <span className="lc-field-label" id="lc-sort-label">
            Sort by
          </span>
          <select
            aria-labelledby="lc-sort-label"
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value)}
          >
            <option value="expirationDate">Expiration date</option>
            <option value="licenseeName">Licensee name</option>
            <option value="companyName">Company</option>
            <option value="licenseType">License type</option>
            <option value="licensingAgencyState">Agency state</option>
          </select>
        </div>

        <label className="lc-toggle">
          <input
            type="checkbox"
            checked={hideCanceled}
            onChange={(e) => setHideCanceled(e.target.checked)}
          />
          Hide canceled
        </label>

        <div className="lc-count">
          {visibleLicenses.length} of {licenseCertificates.length}
        </div>
      </div>

      <div className="lc-table-wrap">
        <table className="lc-table">
          <thead>
            <tr>
              {visibleColumns.map((col) => (
                <th key={col.key}>{col.label}</th>
              ))}
              <th className="lc-col-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td className="lc-empty" colSpan={visibleColumns.length + 1}>
                  Loading licenses...
                </td>
              </tr>
            ) : visibleColumns.length === 0 ? (
              <tr>
                <td className="lc-empty" colSpan={1}>
                  Every column is switched off. Turn some back on in Field Setup.
                </td>
              </tr>
            ) : visibleLicenses.length === 0 ? (
              <tr>
                <td className="lc-empty" colSpan={visibleColumns.length + 1}>
                  {licenseCertificates.length === 0
                    ? "No licenses recorded yet. Use Add License to create the first one."
                    : "No licenses match the current search and filters."}
                </td>
              </tr>
            ) : (
              visibleLicenses.map((item) => {
                const info = expiryInfo(item.expirationDate);
                const rowClass = [
                  info.state === "expired" ? "lc-row-expired" : "",
                  info.state === "soon" ? "lc-row-soon" : "",
                  item.canceled ? "lc-row-canceled" : "",
                ]
                  .filter(Boolean)
                  .join(" ");

                return (
                  <tr key={item.id} className={rowClass}>
                    {visibleColumns.map((col) => (
                      <td key={col.key}>{renderCell(item, col, info)}</td>
                    ))}
                    <td className="lc-col-actions">
                      <div className="button-group">
                        <button
                          className="blue-button"
                          onClick={() => handleDetailsClick(item)}
                        >
                          Details
                        </button>
                        <button
                          className={item.canceled ? "green-button" : "red-button"}
                          onClick={() => requestToggleCanceled(item)}
                        >
                          {item.canceled ? "Reactivate" : "Cancel"}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* ------------------------------------------------ Add popup ------- */}
      {showAddPopup && (
        <ScopedPortalPopup
          onClose={closeAddPopup}
          scopeClass="license-certificates-page lc-popup"
          width={860}
        >
            <h3>Add License / Certificate</h3>
            <p className="popup-lead">
              Fields marked with an asterisk are required. Use Field Setup on the
              main page to change what appears here.
            </p>
            <div className="popup-scroll">
              {renderForm(newLicenseCertificate, handleInputChange, false)}
            </div>
            <div className="button-group">
              <button
                className="green-button"
                onClick={requestAdd}
                disabled={saving || uploading}
              >
                Save
              </button>
              <button className="grey-button" onClick={closeAddPopup}>
                Cancel
              </button>
            </div>
        </ScopedPortalPopup>
      )}

      {/* --------------------------------------------- Details popup ------ */}
      {showDetailsPopup && selectedLicenseCertificate && (
        <ScopedPortalPopup
          onClose={closeDetailsPopup}
          scopeClass="license-certificates-page lc-popup"
          width={860}
        >
            <h3>{isEditing ? "Edit License / Certificate" : "License Details"}</h3>
            <p className="popup-lead">
              {selectedLicenseCertificate.companyName}
              {selectedLicenseCertificate.licenseNumber
                ? ` · No. ${selectedLicenseCertificate.licenseNumber}`
                : ""}
            </p>

            <div className="popup-scroll">
              {isEditing ? (
                renderForm(editedLicenseCertificate, handleInputChangeForEdit, true)
              ) : (
                <div className="popup-detail">
                  <p>
                    <strong>License Type:</strong>{" "}
                    {selectedLicenseCertificate.licenseType === "Other"
                      ? selectedLicenseCertificate.licenseTypeOther
                      : selectedLicenseCertificate.licenseType}
                  </p>
                  <p>
                    <strong>License Category:</strong>{" "}
                    {selectedLicenseCertificate.licenseCategory === "Other"
                      ? selectedLicenseCertificate.licenseCategoryOther
                      : selectedLicenseCertificate.licenseCategory}
                  </p>
                  <p>
                    <strong>Licensee Name:</strong>{" "}
                    {selectedLicenseCertificate.licenseeName === "Other"
                      ? selectedLicenseCertificate.licenseeNameOther
                      : selectedLicenseCertificate.licenseeName}
                  </p>
                  <p>
                    <strong>Company Name on License:</strong>{" "}
                    {selectedLicenseCertificate.companyName}
                  </p>
                  <p>
                    <strong>Classification:</strong>{" "}
                    {selectedLicenseCertificate.classification || "—"}
                  </p>
                  <p>
                    <strong>License Number:</strong>{" "}
                    {selectedLicenseCertificate.licenseNumber}
                  </p>
                  <p>
                    <strong>Expiration Date:</strong>{" "}
                    {formatYmd(selectedLicenseCertificate.expirationDate) || "—"}
                  </p>
                  <p>
                    <strong>Renewal Frequency:</strong>{" "}
                    {selectedLicenseCertificate.frequency === "Other"
                      ? selectedLicenseCertificate.otherFrequency
                      : selectedLicenseCertificate.frequency || "—"}
                  </p>
                  <p>
                    <strong>Renewal Cost:</strong>{" "}
                    {selectedLicenseCertificate.cost
                      ? `$${selectedLicenseCertificate.cost}`
                      : "—"}
                  </p>
                  <p>
                    <strong>State of License:</strong>{" "}
                    {selectedLicenseCertificate.stateOfLicense}
                  </p>
                  <p>
                    <strong>Company Address:</strong>{" "}
                    {selectedLicenseCertificate.companyAddress || "—"}
                  </p>
                  <p>
                    <strong>Agency Name:</strong>{" "}
                    {selectedLicenseCertificate.licensingAgencyName}
                  </p>
                  <p>
                    <strong>Agency Address:</strong>{" "}
                    {selectedLicenseCertificate.licensingAgencyAddress || "—"}
                  </p>
                  {/* The original read licensingAgencycity here - lowercase c -
                      which is not a field that exists, so this line always
                      rendered blank. */}
                  <p>
                    <strong>Agency City:</strong>{" "}
                    {selectedLicenseCertificate.licensingAgencyCity || "—"}
                  </p>
                  <p>
                    <strong>Agency State:</strong>{" "}
                    {selectedLicenseCertificate.licensingAgencyState || "—"}
                  </p>
                  <p>
                    <strong>Agency Zip Code:</strong>{" "}
                    {selectedLicenseCertificate.licensingAgencyZipCode || "—"}
                  </p>
                  <p>
                    <strong>Agency Phone:</strong>{" "}
                    {selectedLicenseCertificate.licensingAgencyPhone || "—"}
                  </p>
                  <p>
                    <strong>Agency Email:</strong>{" "}
                    {selectedLicenseCertificate.licensingAgencyEmail || "—"}
                  </p>
                  <p>
                    <strong>Agency Website:</strong>{" "}
                    {selectedLicenseCertificate.licensingAgencyWebsite || "—"}
                  </p>

                  {fieldConfig.customFields
                    .filter((field) => fieldApplies(field, selectedLicenseCertificate))
                    .map((field) => (
                      <p key={field.id}>
                        <strong>{field.label}:</strong>{" "}
                        {(selectedLicenseCertificate.custom || {})[field.id] || "—"}
                      </p>
                    ))}

                  <p>
                    <strong>Notes:</strong> {selectedLicenseCertificate.notes || "—"}
                  </p>
                  <p>
                    <strong>Status:</strong>{" "}
                    {selectedLicenseCertificate.canceled
                      ? "Canceled"
                      : selectedLicenseCertificate.status}
                  </p>

                  {/* Always rendered. When this was hidden on an empty list
                      there was no way to tell "this licence has no files" from
                      "the upload silently failed". */}
                  <p>
                    <strong>Attachments:</strong>{" "}
                    {(selectedLicenseCertificate.fileUpload || []).length === 0 ? (
                      "None"
                    ) : (
                      <span className="lc-attach-inline">
                        {selectedLicenseCertificate.fileUpload.map((entry, i) => {
                          const file = attachmentInfo(entry, i);
                          if (!file) return null;
                          return (
                            <a
                              key={file.url || i}
                              href={file.url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {file.name}
                            </a>
                          );
                        })}
                      </span>
                    )}
                  </p>
                </div>
              )}
            </div>

            <div className="button-group">
              {isEditing ? (
                <>
                  <button
                    className="green-button"
                    onClick={requestSaveChanges}
                    disabled={saving}
                  >
                    Save changes
                  </button>
                  <button className="grey-button" onClick={() => setIsEditing(false)}>
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <button className="blue-button" onClick={() => setIsEditing(true)}>
                    Edit
                  </button>
                  <button className="red-button" onClick={requestDelete}>
                    Delete
                  </button>
                  <button className="grey-button" onClick={closeDetailsPopup}>
                    Close
                  </button>
                </>
              )}
            </div>
        </ScopedPortalPopup>
      )}

      {/* ---------------------------------------- Field Setup panel ------- */}
      {showFieldPanel && draftConfig && (
        <ScopedPortalPopup
          onClose={() => setShowFieldPanel(false)}
          scopeClass="license-certificates-page lc-popup"
          width={860}
        >
            <h3>Field Setup</h3>
            <p className="popup-lead">
              Change what the Add and Edit forms offer. Nothing here takes effect
              until you save.
            </p>

            <div className="popup-scroll">
              {EDITABLE_DROPDOWNS.map(({ key, label }) => (
                <div className="lc-panel-block" key={key}>
                  <h4>{label}</h4>
                  <p className="lc-panel-note">
                    The choices in this dropdown. "Other" is always available and
                    cannot be removed.
                  </p>

                  <div className="lc-option-list">
                    {draftConfig.dropdowns[key].length === 0 && (
                      <span className="lc-panel-note">No options yet.</span>
                    )}
                    {draftConfig.dropdowns[key].map((option) => (
                      <span className="lc-option-pill" key={option}>
                        {option}
                        <button
                          type="button"
                          className="lc-pill-remove"
                          aria-label={`Remove ${option}`}
                          onClick={() => removeDropdownOption(key, option)}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>

                  <div className="lc-inline-add">
                    <div className="lc-field lc-field-grow">
                      <input
                        value={newOptionText[key] || ""}
                        onChange={(e) =>
                          setNewOptionText((prev) => ({ ...prev, [key]: e.target.value }))
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            addDropdownOption(key);
                          }
                        }}
                        placeholder={`Add an option to ${label}`}
                      />
                    </div>
                    <button className="blue-button" onClick={() => addDropdownOption(key)}>
                      Add
                    </button>
                  </div>
                </div>
              ))}

              <div className="lc-panel-block">
                <h4>Register columns</h4>
                <p className="lc-panel-note">
                  Which columns the Licenses &amp; Certificates table shows, and
                  in what order. Actions is always shown.
                </p>

                <div className="lc-inline-add" style={{ marginBottom: 12 }}>
                  <button className="grey-button" onClick={() => setAllColumns(true)}>
                    Show all
                  </button>
                  <button className="grey-button" onClick={() => setAllColumns(false)}>
                    Hide all
                  </button>
                </div>

                {draftConfig.columns.map((col, index) => (
                  <div className="lc-column-row" key={col.key}>
                    <label className="lc-column-toggle">
                      <input
                        type="checkbox"
                        checked={col.visible}
                        onChange={() => toggleColumn(col.key)}
                      />
                      <span className="lc-column-name">{col.label}</span>
                      {col.key.startsWith("custom:") && (
                        <span className="lc-chip lc-chip-soon">Extra field</span>
                      )}
                    </label>
                    <div className="lc-column-move">
                      <button
                        type="button"
                        className="grey-button"
                        aria-label={`Move ${col.label} up`}
                        onClick={() => moveColumn(col.key, -1)}
                        disabled={index === 0}
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        className="grey-button"
                        aria-label={`Move ${col.label} down`}
                        onClick={() => moveColumn(col.key, 1)}
                        disabled={index === draftConfig.columns.length - 1}
                      >
                        ↓
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="lc-panel-block">
                <h4>Extra fields</h4>
                <p className="lc-panel-note">
                  Add your own fields to the Add and Edit forms. Removing one hides
                  it from the forms; anything already saved on existing licenses is
                  kept.
                </p>

                {draftConfig.customFields.length === 0 ? (
                  <p className="lc-empty-note">
                    No extra fields yet. Add one below and it appears on every
                    license form.
                  </p>
                ) : (
                  <div style={{ marginBottom: 12 }}>
                    {draftConfig.customFields.map((field) => (
                      <div className="lc-custom-row" key={field.id}>
                        <div className="lc-custom-row-main">
                          <span className="lc-strong">{field.label}</span>
                          <span className="lc-sub">
                            {CUSTOM_FIELD_TYPES.find((t) => t.value === field.type)?.label}
                            {field.type === "select" && (field.options || []).length
                              ? ` · ${field.options.join(", ")}`
                              : ""}
                          </span>
                          {describeCondition(field) && (
                            <span className="lc-sub lc-cond-note">
                              {describeCondition(field)}
                            </span>
                          )}
                        </div>
                        <button
                          className="red-button"
                          onClick={() => removeCustomField(field.id)}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="lc-inline-add">
                  <div className="lc-field lc-field-grow">
                    <span className="lc-field-label">Field title</span>
                    <input
                      value={newCustomField.label}
                      onChange={(e) =>
                        setNewCustomField((prev) => ({ ...prev, label: e.target.value }))
                      }
                      placeholder="e.g. Renewal contact"
                    />
                  </div>
                  <div className="lc-field">
                    <span className="lc-field-label">Field type</span>
                    <select
                      value={newCustomField.type}
                      onChange={(e) =>
                        setNewCustomField((prev) => ({ ...prev, type: e.target.value }))
                      }
                    >
                      {CUSTOM_FIELD_TYPES.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  {newCustomField.type === "select" && (
                    <div className="lc-field lc-field-grow">
                      <span className="lc-field-label">Choices</span>
                      <input
                        value={newCustomField.options}
                        onChange={(e) =>
                          setNewCustomField((prev) => ({
                            ...prev,
                            options: e.target.value,
                          }))
                        }
                        placeholder="Separate with commas"
                      />
                    </div>
                  )}
                </div>

                {/* Conditional visibility. Leave the dropdown on "Always show"
                    for a field that should appear on every license. */}
                <div className="lc-cond-builder">
                  <div className="lc-inline-add">
                    <div className="lc-field">
                      <span className="lc-field-label">Show this field</span>
                      <select
                        value={newCustomField.conditionField}
                        onChange={(e) =>
                          setNewCustomField((prev) => ({
                            ...prev,
                            conditionField: e.target.value,
                            conditionValues: [],
                            conditionDraftValue: "",
                          }))
                        }
                      >
                        <option value="">Always show</option>
                        {CONDITION_PARENTS.map((p) => (
                          <option key={p.key} value={p.key}>
                            Only when {p.label} is...
                          </option>
                        ))}
                      </select>
                    </div>

                    {newCustomField.conditionField && (
                      <>
                        <div className="lc-field lc-field-grow">
                          <span className="lc-field-label">Matching value</span>
                          <select
                            value={newCustomField.conditionDraftValue}
                            onChange={(e) =>
                              setNewCustomField((prev) => ({
                                ...prev,
                                conditionDraftValue: e.target.value,
                              }))
                            }
                          >
                            <option value="">Select a value</option>
                            {(draftConfig.dropdowns[newCustomField.conditionField] || []).map(
                              (o) => (
                                <option key={o} value={o}>
                                  {o}
                                </option>
                              )
                            )}
                            <option value="Other">Other</option>
                          </select>
                        </div>
                        <button
                          className="grey-button"
                          onClick={addConditionValue}
                          disabled={!newCustomField.conditionDraftValue}
                        >
                          Add value
                        </button>
                      </>
                    )}
                  </div>

                  {newCustomField.conditionValues.length > 0 && (
                    <div className="lc-option-list" style={{ marginTop: 10 }}>
                      {newCustomField.conditionValues.map((v) => (
                        <span className="lc-option-pill" key={v}>
                          {v}
                          <button
                            type="button"
                            className="lc-pill-remove"
                            aria-label={`Remove ${v}`}
                            onClick={() => removeConditionValue(v)}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}

                  {newCustomField.conditionField &&
                    newCustomField.conditionValues.length === 0 && (
                      <p className="lc-hint">
                        Pick at least one value, or the field is saved as always
                        visible.
                      </p>
                    )}
                </div>

                <div className="lc-inline-add" style={{ marginTop: 12 }}>
                  <button
                    className="blue-button"
                    onClick={addCustomField}
                    disabled={!newCustomField.label.trim()}
                  >
                    Add field
                  </button>
                </div>
              </div>
            </div>

            <div className="button-group">
              <button
                className="green-button"
                onClick={requestSaveFieldConfig}
                disabled={saving}
              >
                Save
              </button>
              <button className="grey-button" onClick={() => setShowFieldPanel(false)}>
                Cancel
              </button>
            </div>
        </ScopedPortalPopup>
      )}

      {/* --------------------------------------------- Confirm sheet ------ */}
      {confirmState && (
        <ScopedPortalPopup
          onClose={closeConfirm}
          scopeClass="license-certificates-page lc-popup"
          level={1}
          width={460}
          role="alertdialog"
        >
            <h3>{confirmState.title}</h3>
            <p className="popup-lead">{confirmState.message}</p>
            <div className="button-group">
              {confirmState.confirmLabel && (
                <button
                  className={confirmState.tone === "red" ? "red-button" : "green-button"}
                  onClick={confirmState.onConfirm}
                  disabled={saving}
                >
                  {saving ? "Working..." : confirmState.confirmLabel}
                </button>
              )}
              <button className="grey-button" onClick={closeConfirm} disabled={saving}>
                {confirmState.cancelLabel || "Cancel"}
              </button>
            </div>
        </ScopedPortalPopup>
      )}
    </div>
  );
};

export default LicenseCertificate;
